import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSnackBar} from '@angular/material/snack-bar';
import {catchError, of, switchMap} from 'rxjs';
import {SharedModule} from '../../../../shared/shared.module';
import {
  guidValidator,
  normalizeGuid,
  notFutureDateTimeValidator,
  requiredTrimmedValidator,
} from '../../../../shared/validators/erp-manual-update.validators';
import {TmtErpManualUpdateService} from '../../service/tmt-erp-manual-update.service';

export interface TmtErpManualUpdateDialogData {
  salesOrderId: number;
  tripReference: string | null;
  existingResponse?: {
    erpResponseId: number;
    erpMessageId: string | null;
    erpOrderNumber: string | null;
    erpMessageTimestamp: string | null;
    result: string | null;
    errorDescription: string | null;
  } | null;
}

const RESULT_OPTIONS = ['SUCCESS', 'PENDING', 'ERROR'];

// Matches SalesOrderErpResponse.ErpOrderNumber NVARCHAR(50) in fms-tmt-sql.
const ERP_ORDER_NUMBER_MAX_LENGTH = 50;

/**
 * Lets a user manually record that a sales order was pushed to ERP outside FMS (e.g. sent by
 * hand in TMT/D365 UAT where ERP outbound isn't enabled). Performs the same two steps the real
 * outbound pipeline performs automatically once it has a message id: stamp the pending
 * sync-queue row(s) as sent (so the next TMT change is captured as a new delta instead of being
 * folded into this one), then record the ERP response against that same queue row.
 */
@Component({
  selector: 'app-tmt-erp-manual-update-dialog',
  imports: [
    MatButton,
    MatCheckbox,
    MatExpansionModule,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './tmt-erp-manual-update-dialog.component.html',
  styles: [`
    /* Fully custom layout for this dialog only — not shared with modal-panel/other popups. */
    .manual-update__fields {
      display: flex;
      flex-direction: column;
      gap: 18px;
      width: 100%;
      box-sizing: border-box;
      min-width: 420px;
    }
    .manual-update__field {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    mat-form-field {
      width: 100%;
    }
    /* mat-hint is reserved for short field-state messages (validation errors) only; long
       explanatory text lives in .manual-update__field-note below, in normal document flow, so
       it can never overlap the mat-form-field's own hint/error slot or the next control. */
    ::ng-deep .manual-update__field .mat-mdc-form-field-subscript-wrapper {
      position: static;
      height: auto;
      overflow: visible;
    }
    .manual-update__field-note {
      font-size: 12px;
      color: rgba(0, 0, 0, .6);
      margin: 4px 0 0;
      line-height: 1.4;
    }
    .manual-update__hint {
      font-size: 12px;
      color: rgba(0, 0, 0, .6);
      margin: -4px 0 8px;
    }
    .manual-update__warning {
      color: #ed6c02;
      font-size: 13px;
      margin: 6px 0 0;
    }
    .manual-update__info {
      font-size: 12.5px;
      margin: 0;
      padding-left: 18px;
    }
    .manual-update__info li {
      margin-bottom: 6px;
    }
  `],
})
export class TmtErpManualUpdateDialogComponent implements OnInit {
  protected readonly data = inject<TmtErpManualUpdateDialogData>(MAT_DIALOG_DATA);
  protected readonly resultOptions = RESULT_OPTIONS;

  private readonly dialogRef = inject(MatDialogRef<TmtErpManualUpdateDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly manualUpdateService = inject(TmtErpManualUpdateService);
  private readonly snackBar = inject(MatSnackBar);

  protected form!: FormGroup;
  protected loading = true;
  protected saving = false;
  protected noPendingChanges = false;
  protected pendingCount = 0;
  protected pendingFailedCount = 0;
  protected readonly erpOrderNumberMaxLength = ERP_ORDER_NUMBER_MAX_LENGTH;
  protected readonly nowInputValue = this.toLocalDateTimeInputValue(new Date());
  // An existing response row means this is a correction/edit of a previous manual (or real)
  // entry, not a brand-new one — drives the dialog title, the reused ERP Message Id default,
  // and whether the old row gets deleted before the new one is saved (see submit()).
  protected readonly isEditing = !!this.data.existingResponse;

  ngOnInit(): void {
    const existing = this.data.existingResponse;

    this.form = this.fb.group({
      erpMessageId: [
        existing?.erpMessageId ? normalizeGuid(existing.erpMessageId) : this.generateGuid(),
        [Validators.required, guidValidator()],
      ],
      erpOrderNumber: [
        existing?.erpOrderNumber ?? null,
        [Validators.required, requiredTrimmedValidator(), Validators.maxLength(ERP_ORDER_NUMBER_MAX_LENGTH)],
      ],
      erpMessageTimestamp: [
        existing?.erpMessageTimestamp
          ? this.toLocalDateTimeInputValue(new Date(existing.erpMessageTimestamp))
          : this.toLocalDateTimeInputValue(new Date()),
        [Validators.required, notFutureDateTimeValidator()],
      ],
      result: [existing?.result ?? 'SUCCESS', Validators.required],
      errorDescription: [existing?.errorDescription ?? null],
      // Defaults to false (conservative): checking it also force-clears any pending FAILED rows
      // as if they were now resolved, even though nothing about the actual failure was fixed.
      // Only check it once you're sure this manual entry truly covers every pending change,
      // including the failed one(s) below.
      isFullSend: [false],
    });

    // Requiring an explanation whenever a response is recorded as ERROR is a UI-side rule (the
    // DB column itself is nullable) — surfaces the same expectation a real ERP error response
    // would carry, so a manual "ERROR" entry can't be left without any context for why.
    this.form.get('result')!.valueChanges.subscribe((result: string) => {
      const errorDescriptionControl = this.form.get('errorDescription')!;
      errorDescriptionControl.setValidators(result === 'ERROR' ? [Validators.required, requiredTrimmedValidator()] : []);
      errorDescriptionControl.updateValueAndValidity();
    });

    this.manualUpdateService.getPendingSyncQueue(this.data.salesOrderId).subscribe({
      next: (items) => {
        this.noPendingChanges = !items?.length;
        this.pendingCount = items?.length ?? 0;
        this.pendingFailedCount = items?.filter((i) => i.isFailed).length ?? 0;
        this.loading = false;
      },
      error: () => {
        // Fail open — the submit step re-checks and will surface any real error there.
        this.loading = false;
      },
    });
  }

  protected regenerateMessageId(): void {
    this.form.get('erpMessageId')!.setValue(this.generateGuid());
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected submit(): void {
    if (this.form.invalid || this.saving) return;

    this.saving = true;
    const raw = this.form.getRawValue();
    const erpMessageTimestamp = new Date(raw.erpMessageTimestamp).toISOString();
    const erpMessageId = normalizeGuid(raw.erpMessageId);
    const existingResponseId = this.data.existingResponse?.erpResponseId;

    // If editing, delete the old response row first — Upsert only overwrites in place when the
    // existing row's Result is still "PENDING"; once it's SUCCESS/ERROR, resubmitting with the
    // same ErpMessageId would insert a second row instead of correcting the first. Deleting first
    // guarantees a clean edit no matter what the prior Result was.
    of(null).pipe(
      switchMap(() => existingResponseId
        ? this.manualUpdateService.deleteErpResponse(existingResponseId)
        : of(null)),
      switchMap(() => this.manualUpdateService.getPendingSyncQueue(this.data.salesOrderId)),
      switchMap((pendingRows) => {
        const syncQueueId = pendingRows?.length
          ? Math.max(...pendingRows.map((r) => r.id))
          : null;

        return this.manualUpdateService.updateErpProps({
          salesOrderId: this.data.salesOrderId,
          erpMessageId,
          erpMessageTimestamp,
          isFullSend: raw.isFullSend,
        }).pipe(
          switchMap(() => this.manualUpdateService.upsertErpResponse({
            queueName: 'Manual',
            erpMessageId,
            salesOrderSyncQueueId: syncQueueId,
            erpOrderNumber: raw.erpOrderNumber,
            result: raw.result,
            errorDescription: raw.errorDescription || null,
          })),
        );
      }),
      catchError((err: Error) => {
        this.snackBar.open(err.message || 'Failed to save manual ERP update', 'Dismiss', {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
        return of(null);
      }),
    ).subscribe((result) => {
      this.saving = false;
      if (result === null) return;
      this.snackBar.open('Manual ERP update saved', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      this.dialogRef.close({updated: true});
    });
  }

  private generateGuid(): string {
    return crypto.randomUUID();
  }

  private toLocalDateTimeInputValue(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
